import * as React from 'react';
import { useEffect } from 'react';
import { Controller, useFieldArray, useFormContext } from 'react-hook-form';

import { Trash2Icon } from 'lucide-react';

import { Button } from 'components/Button';
import { Label } from 'components/Label';
import { Textarea } from 'components/Textarea';
import { Flex, Grid } from 'components/layout';
import { Typography } from 'components/typography';
import { NormalizedLoad } from 'types/Load';
import { ButtonNamePosthog } from 'types/enums/CustomPosthogEvents';

import { MGBLoadNoteType } from './types';
import { createMGBNote } from './utils';

export default function NotesForm(): React.JSX.Element {
  const { control, setValue } = useFormContext<NormalizedLoad>();
  const { fields, append, remove } = useFieldArray({
    control,
    name: 'notes',
  });

  useEffect(() => {
    fields.forEach((_, index) => {
      setValue(`notes.${index}.noteType`, MGBLoadNoteType.InternalNote, {
        shouldDirty: false,
        shouldValidate: false,
      });
    });
  }, [fields, setValue]);

  return (
    <>
      {fields.length === 0 ? (
        <Typography variant='body-sm' textColor='muted'>
          No notes added yet.
        </Typography>
      ) : null}

      {fields.map((field, index) => (
        <div
          key={field.id}
          className='rounded border border-neutral-200 p-3 mb-3 last:mb-0'
        >
          <Flex align='center' justify='between' className='mb-3'>
            <Typography variant='body-sm' weight='semibold'>
              Internal Note #{index + 1}
            </Typography>
            <Button
              buttonNamePosthog={ButtonNamePosthog.MGBRemoveNote}
              type='button'
              variant='ghost'
              size='xs'
              onClick={() => remove(index)}
              aria-label={`Remove note ${index + 1}`}
            >
              <Trash2Icon className='h-4 w-4' />
            </Button>
          </Flex>

          <Grid cols='4' gap='lg' className='mx-0 w-full'>
            <div className='col-span-4'>
              <Flex direction='col' className='w-full'>
                <Label name={`notes.${index}.note`}>Note</Label>
                <Controller
                  name={`notes.${index}.note`}
                  control={control}
                  render={({ field: f }) => (
                    <Textarea
                      className='mt-1'
                      value={f.value ?? ''}
                      onChange={f.onChange}
                      onBlur={f.onBlur}
                      placeholder='Internal note for the load'
                    />
                  )}
                />
              </Flex>
            </div>
          </Grid>
        </div>
      ))}

      <Flex align='center' justify='end' className='mr-px mt-1'>
        <Button
          buttonNamePosthog={ButtonNamePosthog.MGBAddNote}
          type='button'
          variant='ghost'
          size='sm'
          onClick={() => append(createMGBNote())}
          className='h-8 px-3 text-xs text-brand-500 hover:-mx-px'
        >
          + Add Internal Note
        </Button>
      </Flex>
    </>
  );
}
